import { setActiveTeamIdForRequests } from "@frontend/utils/active_team_header.client";
import { registerPushIfGranted } from "@frontend/utils/push.utils";
import { type ReactNode, useEffect } from "react";
import type { SessionInfo } from "./UserContext";

interface WorkspaceProviderProps {
	children: ReactNode;
	sessionInfo?: SessionInfo | null;
}

/**
 * Signed-in shell effects for the deployment's single hidden workspace
 * (ADR-023). Users never see or switch it; it exists only so team-scoped
 * endpoints get their `x-team-id` header. `authLoader` seeds the header cache
 * before the first render (resolving `teams.getDefaultTeam` when the session
 * has no active team yet); this provider keeps it in step with the session.
 */
export function WorkspaceProvider({ children, sessionInfo }: WorkspaceProviderProps) {
	const userId = sessionInfo?.user?.id;
	const activeTeamAppId = sessionInfo?.user?.activeTeamAppId ?? null;

	useEffect(() => {
		if (!userId) return;
		setActiveTeamIdForRequests(activeTeamAppId);
	}, [userId, activeTeamAppId]);

	// Silent FCM registration on login (Novu push notifications). NEVER
	// prompts for permission — browsers permanently disable
	// Notification.requestPermission() for the origin if the user dismisses a
	// bad prompt once. The user-gesture version (promptAndRegisterPush) lives
	// behind explicit UI: the profile toggle and the notification banner.
	useEffect(() => {
		if (!userId) return;
		void registerPushIfGranted();
	}, [userId]);

	return children;
}
